import { useMemo, useState } from 'react';
import WizardTextInputControlledComponent from 'components/v2/epd-wizard-inputs/WizardTextInputControlled';
import { DownloadButton, PcrListItem, PcrListItemHeader } from 'components/v2/pcr-selector/styled';
import { useTranslation } from 'react-i18next';
import { PCRModel } from 'services/EpdClient';
import FileService from 'services/FileService';
import styled from 'styled-components';
import { A } from 'styles/Styles.styled';
import { isPcrExpired } from 'util/utils';
import MyRadioButton from 'components/controls/MyRadioButton';

interface TProps {
  cpcrList: PCRModel[];
  selectedCpcr: PCRModel | null;
  onSelectedCpcrChange(cpcr: PCRModel | null): void;
}

const CPcrSelectorDialogForm = ({ cpcrList, selectedCpcr, onSelectedCpcrChange }: TProps) => {
  const { t } = useTranslation();

  const [searchTerm, setSearchTerm] = useState('');
  const [nowEpoch] = useState<number>(Date.now());

  const filteredCpcrList = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return cpcrList;

    return cpcrList.filter(
      (item) =>
        item.name?.toLowerCase().includes(term) ||
        item.shortName?.toLowerCase().includes(term) ||
        item.fullName?.toLowerCase().includes(term) ||
        item.cpcName?.toLowerCase().includes(term) ||
        item.combinedName?.toLowerCase().includes(term)
    );
  }, [cpcrList, searchTerm]);

  const handleSearch = (event: any) => {
    setSearchTerm(event.target.value ?? '');
  };

  return (
    <>
      <WizardTextInputControlledComponent
        label="C-PCR name"
        tooltipPosition={'right'}
        value={undefined}
        placeholder="Type a C-PCR name for a quick search"
        onChange={handleSearch}
        onChanged={() => {}}
        name="title"
        maxLength={150}
      />

      <ListContainer>
        {filteredCpcrList && filteredCpcrList.length > 0 ? (
          filteredCpcrList.map((item) => {
            const isExpired = isPcrExpired(item.validUntil, nowEpoch);
            const isSelected = selectedCpcr?.id === item.id;
            const hasDocuments = !!item.documents && item.documents.length > 0;

            return (
              <PcrListItem key={item.id} $selected={isSelected} $disabled={isExpired}>
                <PcrListItemHeader $disabled={isExpired}>
                  <span style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>{item.fullName}</span>
                  <MyRadioButton
                    inputId={item.id}
                    name="pcrItem"
                    value={item.id!}
                    checked={isSelected}
                    onChange={() => onSelectedCpcrChange(item)}
                    disabled={isExpired}
                  />
                </PcrListItemHeader>

                {item.cpCs && (
                  <div style={{ opacity: isExpired ? 0.5 : undefined }}>
                    {item.cpCs.map((cpc) => (
                      <div style={{ padding: '0.5rem 0 0 0 ' }} key={cpc.id}>
                        {cpc.code} {cpc.description}
                      </div>
                    ))}
                  </div>
                )}

                <BottomRow>
                  <BottomLeft>
                    {hasDocuments &&
                      item.documents!.map(
                        (file, index) =>
                          file && (
                            <div key={index}>
                              <div style={{ padding: '0.5rem 0 0 0 ', opacity: isExpired ? 0.5 : undefined }}>
                                <span>{file?.name || t('epdWizard.wizardStep1.pcrMenu.placeholderImage')}</span>
                              </div>
                              <A target="_blank" href={FileService.getImageUrl(file?.id || '')}>
                                <DownloadButton style={{ marginTop: '1rem' }} label="Download c-PCR" />
                              </A>
                            </div>
                          )
                      )}
                  </BottomLeft>

                  {isExpired && <ExpiredTag>Expired</ExpiredTag>}
                </BottomRow>
              </PcrListItem>
            );
          })
        ) : (
          <div style={{ padding: '1rem', fontStyle: 'italic' }}>
            {'No items in this category or category is not selected'}
          </div>
        )}
      </ListContainer>
    </>
  );
};

const ListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
`;

const ExpiredTag = styled.div`
  padding: 2px 4px;
  background-color: ${(props) => props.theme.colors.statusLabelRedBg};
  color: ${(props) => props.theme.colors.statusLabelRedText};
  font-size: 10px;
  font-weight: 500;
  line-height: 1.25;
  text-transform: uppercase;
`;

const BottomRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: auto;
`;

const BottomLeft = styled.div`
  min-height: 1px;
`;

export default CPcrSelectorDialogForm;
